import React, { useState } from 'react';
import { formatCheckId, statusBadgeClass } from './readinessUtils';
import { HealConfirmModal } from './HealConfirmModal';
import { ReadinessFixGuide } from './ReadinessFixGuide';
import { getReadinessFixGuide, shouldShowReadinessFixGuide } from './readinessFixRoutes';

function EvidencePanel({ evidence }) {
  if (!evidence || typeof evidence !== 'object' || !Object.keys(evidence).length) {
    return null;
  }
  return (
    <pre className="small bg-light border rounded p-2 mt-2 mb-0 overflow-auto" style={{ maxHeight: '180px' }}>
      {JSON.stringify(evidence, null, 2)}
    </pre>
  );
}

function FixSteps({ remediation, check }) {
  const steps = remediation?.fix_steps;
  const showGuide = shouldShowReadinessFixGuide(check);
  const guide = showGuide ? getReadinessFixGuide(check) : null;
  const showPlaybookSteps = Array.isArray(steps) && steps.length && !(guide?.instructions?.length);
  if (!showPlaybookSteps && !showGuide) return null;
  return (
    <div className="mt-2">
      {showPlaybookSteps ? (
        <>
          <div className="small fw-semibold">Fix steps</div>
          <ul className="small mb-0 ps-3">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        </>
      ) : null}
      {showGuide ? <ReadinessFixGuide check={check} remediation={remediation} /> : null}
    </div>
  );
}

export function ReadinessCheckRow({ check, onHealSuccess }) {
  const [showEvidence, setShowEvidence] = useState(false);
  const [healOpen, setHealOpen] = useState(false);

  return (
    <>
      <div className="border rounded p-3 mb-2 bg-white">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
          <div className="flex-grow-1">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span className={`badge ${statusBadgeClass(check.status, check.severity)}`}>
                {check.severity === 'info' && check.status === 'pass' ? 'info' : check.status}
              </span>
              <span className="fw-semibold">{formatCheckId(check.id)}</span>
              <span className="text-muted small">Layer {check.layer}</span>
            </div>
            <div className="small">{check.message}</div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowEvidence((v) => !v)}>
              {showEvidence ? 'Hide evidence' : 'View evidence'}
            </button>
            {check.auto_heal ? (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setHealOpen(true)}>
                Heal
              </button>
            ) : null}
          </div>
        </div>
        {showEvidence ? <EvidencePanel evidence={check.evidence} /> : null}
        <FixSteps remediation={check.remediation} check={check} />
      </div>
      <HealConfirmModal
        open={healOpen}
        actionId={check.auto_heal}
        checkId={check.id}
        onClose={() => setHealOpen(false)}
        onSuccess={() => {
          setHealOpen(false);
          onHealSuccess?.();
        }}
      />
    </>
  );
}

export function ReadinessCheckGroup({ title, tone, checks, defaultOpen = true, onHealSuccess }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!checks?.length) return null;
  return (
    <div className={`card border-${tone} mb-3`}>
      <div className={`card-header bg-${tone}-subtle d-flex align-items-center justify-content-between`}>
        <div className="fw-semibold">
          {title} <span className="badge text-bg-light border ms-1">{checks.length}</span>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen((v) => !v)}>
          {open ? 'Collapse' : 'Expand'}
        </button>
      </div>
      {open ? (
        <div className="card-body pt-3">
          {checks.map((check) => (
            <ReadinessCheckRow key={check.id} check={check} onHealSuccess={onHealSuccess} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
